import { ILead, VibechatMetricResponse, StatusCounterLead } from "../../types/lead.type";
import { ApiResponseType, makeRequest } from "../helpers/api.helpers";
import { createDebounce } from "../helpers/debounce.helper";
import { getCompanyId } from "./company.api";

const API_URL = import.meta.env.VITE_API_URL;

const makeRequestDebounced = createDebounce(makeRequest, 200);

export async function getLeads({
  status = [],
  search = "",
  startDate = "",
  endDate = "",
  startDateAtualizadoEm = "",
  endDateAtualizadoEm = "",
  page = 1,
  itemsPerPage = 100,
  orderBy = "",
  leadOrigin = "",
  technicalConsultant = "",
  fields = "companyId, name, email, cpf, cnh, pendencies, groupName, jurValidation, status, updatedAt, createdAt, leadOrigin, technicalConsultant, cpfDoIndicador, whatsapp, externalReferences",
} = {}) {
  const params = new URLSearchParams({
    page: String(page),
    itemsPerPage: String(itemsPerPage),
    fields,
  });

  let statusParam = "";
  if (Array.isArray(status) && status.length > 0) {
    statusParam = status.join(",");
  } else if (typeof status === "string") {
    statusParam = status;
  }
  status && params.append("filters[status]", statusParam);
  search && params.append("search", search);
  orderBy && params.append("orderBy", orderBy);
  startDate && params.append("filters[startDate]", startDate.toString());
  endDate && params.append("filters[endDate]", endDate.toString());
  startDateAtualizadoEm && params.append("filters[startDateAtualizadoEm]", startDateAtualizadoEm.toString());
  endDateAtualizadoEm && params.append("filters[endDateAtualizadoEm]", endDateAtualizadoEm.toString());
  leadOrigin && params.append("filters[leadOrigin]", leadOrigin);
  technicalConsultant && params.append("filters[technicalConsultant]", technicalConsultant);

  const url = `${API_URL}/leads?${params.toString()}`;
  const result = (await makeRequestDebounced(url)) as ApiResponseType<ILead[]> & {
    totalItems: number;
    statusCounter: StatusCounterLead;
  };

  return {
    ok: true,
    data: result.data,
    totalItems: result.totalItems,
    statusCounter: result.statusCounter,
  };
}
export async function getVibechatMetrics(startDate?: string, endDate?: string) {
  const params = new URLSearchParams();
  if (startDate) params.append("startDate", startDate);
  if (endDate) params.append("endDate", endDate);

  const queryString = params.toString();
  const url = `${API_URL}/leads/pending-alerts${queryString ? `?${queryString}` : ""}`;

  const result = (await makeRequestDebounced(url)) as ApiResponseType<VibechatMetricResponse[]>;

  return result;
}

export async function getDashboard({ startDate = "", endDate = "" }) {
  const params = new URLSearchParams({ startDate, endDate });

  const url = `${API_URL}/leads/dashboard/customer?${params.toString()}`;
  return (await makeRequestDebounced(url)) as ApiResponseType<unknown>;
}

export async function getLeadById(leadId: string) {
  const url = `${API_URL}/leads/${leadId}`;

  const result = (await makeRequestDebounced(url)) as Promise<ApiResponseType<ILead>>;

  return result;
}

export async function createLead(leadData) {
  const data = {
    ...leadData,
    companyId: getCompanyId(),
  };
  const url = `${API_URL}/leads`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };
  return makeRequestDebounced(url, options) as Promise<ApiResponseType<ILead & { exist: boolean }>>;
}

const removeReadonlyFields = (updatedData) => {
  delete updatedData._id;
  delete updatedData.__v;
  delete updatedData.contacts;
  delete updatedData.pendencies;
  // delete updatedData.status;
  delete updatedData.updatedAt;
  delete updatedData.createdAt;
};

export async function updateLead(id: string, updatedData: Partial<ILead>) {
  removeReadonlyFields(updatedData);
  const url = `${API_URL}/leads/${id}`;
  const options = {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updatedData),
  };
  const result = makeRequestDebounced(url, options) as Promise<ApiResponseType<ILead>>;

  return result;
}

export async function updateLeadProps(id: string, updatedData: Partial<ILead>) {
  removeReadonlyFields(updatedData);
  const url = `${API_URL}/leads/${id}`;
  const options = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updatedData),
  };

  const result = makeRequestDebounced(url, options) as Promise<ApiResponseType<ILead>>;

  return result;
}

export async function updateLeadCommercialActions(id: string, updatedData: Partial<ILead>) {
  // remove readonly fields
  //removeReadonlyFields(updatedData);

  const url = `${API_URL}/leads/commercial/${id}`;
  const options = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updatedData),
  };

  const result = makeRequestDebounced(url, options) as Promise<ApiResponseType<ILead>>;

  return result;
}

export async function convertLeadToRenter({ leadId }) {
  const url = `${API_URL}/leads/${leadId}/to-renter`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ leadId }),
  };
  return makeRequestDebounced(url, options) as Promise<ApiResponseType<ILead>>;
}

export async function startConversation(leadId: string) {
  const url = `${API_URL}/leads/${leadId}/start-conversation`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  };
  const result = makeRequestDebounced(url, options) as Promise<ApiResponseType<string>>;

  return result;
}

export async function startConversationVibechat(leadId: string) {
  const url = `${API_URL}/leads/${leadId}/start-vibechat`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ leadId: leadId }),
  };
  const result = makeRequestDebounced(url, options) as Promise<{
    data: { conversationId: string; chatUrl: string };
  }>;
  return result;
}

export async function deleteLeadDuplicate(data: { cpf: string; reason: string }) {
  const url = `${API_URL}/leads/delete-duplicates`;

  const options = {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };

  return makeRequest(url, options) as Promise<{
    ok: boolean;
    message?: string;
  }>;
}
export async function updateContactsLabelVibechat({ contactIds, labels }) {
  const url = `${API_URL}/vibechat/update-labels`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ contactIds: contactIds, labels }),
  };

  const result = makeRequestDebounced(url, options) as Promise<{ ok: boolean }>;
  return result;
}
